"use client";

import { useEffect, useRef, useState } from "react";
import { TextAreaField } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { StyledResult } from "@/components/tools/text/styled-result";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { t } from "@/lib/i18n/interpolate";
import {
  applyStyle,
  DOUBLE_STRUCK_EXCEPTIONS,
  DOUBLE_STRUCK_MAP,
  unstyledCharacters,
} from "@/lib/tool-kit/text/unicode-styles";

/** The blackboard-bold symbols mathematicians actually use, with their LaTeX. */
const SYMBOLS = [
  { id: "naturals", char: "ℕ", latex: "\\mathbb{N}" },
  { id: "integers", char: "ℤ", latex: "\\mathbb{Z}" },
  { id: "rationals", char: "ℚ", latex: "\\mathbb{Q}" },
  { id: "reals", char: "ℝ", latex: "\\mathbb{R}" },
  { id: "complex", char: "ℂ", latex: "\\mathbb{C}" },
  { id: "quaternions", char: "ℍ", latex: "\\mathbb{H}" },
  { id: "primes", char: "ℙ", latex: "\\mathbb{P}" },
  { id: "expectation", char: "\u{1D53C}", latex: "\\mathbb{E}" },
  { id: "field", char: "\u{1D53D}", latex: "\\mathbb{F}" },
  { id: "fieldK", char: "\u{1D55C}", latex: "\\Bbbk" },
  { id: "indicator", char: "\u{1D7D9}", latex: "\\mathbbm{1}" },
] as const;

type SymbolId = (typeof SYMBOLS)[number]["id"];

/** English strings. A translated content file overrides these through `ui`. */
export const DOUBLE_STRUCK_TEXT_GENERATOR_STRINGS = {
  inputLabel: "Your text",
  inputPlaceholder: "Type or paste the text you want in double-struck letters",
  trySample: "Try a sample",
  sample: "Sets N, Z, Q, R and C",
  clear: "Clear",
  results: "Results",
  resultLabel: "Double-struck (𝕓𝕝𝕒𝕔𝕜𝕓𝕠𝕒𝕣𝕕 𝕓𝕠𝕝𝕕)",
  exceptionsHint:
    "{letters} come from Unicode's older Letterlike Symbols block, which is where they have always been encoded. In some fonts they look a little different from the other letters.",
  emptyResult: "Type above to see the result.",
  copy: "Copy",
  copied: "Copied!",
  unchanged:
    "Left as they were — Unicode has no double-struck version of these: {list}",
  symbolsHeading: "Blackboard-bold symbols",
  symbolsHint:
    "Select a symbol to copy it. The LaTeX command is shown below each one.",
  copySymbol: "Copy {symbol} ({meaning})",
  copiedSymbol: "Copied {symbol}",
  meanings: {
    naturals: "Natural numbers",
    integers: "Integers",
    rationals: "Rational numbers",
    reals: "Real numbers",
    complex: "Complex numbers",
    quaternions: "Quaternions",
    primes: "Primes or probability",
    expectation: "Expected value",
    field: "A field, as in 𝔽ₚ",
    fieldK: "A ground field k",
    indicator: "Indicator function",
  } as Record<SymbolId, string>,
  accessibility:
    'Screen readers may announce these as math symbols ("double-struck capital R") or skip them, and search treats them as different characters from ordinary letters — fine for mathematics, poor for prose people need to read.',
} as const;

type Strings = typeof DOUBLE_STRUCK_TEXT_GENERATOR_STRINGS;

function SymbolPalette({ strings }: { strings: Strings }) {
  const [copiedId, setCopiedId] = useState<SymbolId | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    },
    []
  );

  async function copy(id: SymbolId, char: string) {
    if (!(await writeToClipboard(char))) {
      return;
    }
    setCopiedId(id);
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => setCopiedId(null), 2000);
  }

  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.symbolsHeading}</h3>
      <p className={HINT}>{strings.symbolsHint}</p>
      <ul className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {SYMBOLS.map((symbol) => {
          const meaning = strings.meanings[symbol.id];
          return (
            <li key={symbol.id}>
              <button
                aria-label={t(strings.copySymbol, {
                  symbol: symbol.char,
                  meaning,
                })}
                className="flex w-full flex-col items-center gap-1 rounded-lg border border-border bg-card px-2 py-3 text-center transition-colors hover:bg-muted"
                onClick={() => copy(symbol.id, symbol.char)}
                type="button"
              >
                <span aria-hidden="true" className="text-3xl text-foreground">
                  {symbol.char}
                </span>
                <span className="text-foreground text-xs">
                  {copiedId === symbol.id
                    ? t(strings.copiedSymbol, { symbol: symbol.char })
                    : meaning}
                </span>
                <code className="text-[11px] text-muted-foreground">
                  {symbol.latex}
                </code>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const EXCEPTION_LETTERS = Object.values(DOUBLE_STRUCK_EXCEPTIONS).join(" ");

export default function DoubleStruckTextGeneratorWidget() {
  const strings = useToolUi(DOUBLE_STRUCK_TEXT_GENERATOR_STRINGS);
  const [input, setInput] = useState("");
  const styled = applyStyle(input, DOUBLE_STRUCK_MAP);
  const unchanged = unstyledCharacters(input, DOUBLE_STRUCK_MAP);

  return (
    <>
      <TextAreaField
        id="dstruck-input"
        label={strings.inputLabel}
        onChange={setInput}
        placeholder={strings.inputPlaceholder}
        prose
        rows={4}
        value={input}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(strings.sample)}
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={input.length === 0}
          onClick={() => setInput("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <section aria-live="polite" className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <StyledResult
          copiedLabel={strings.copied}
          copyLabel={strings.copy}
          emptyText={strings.emptyResult}
          hint={t(strings.exceptionsHint, { letters: EXCEPTION_LETTERS })}
          label={strings.resultLabel}
          value={styled}
        />
        {unchanged.length > 0 ? (
          <p className={HINT}>
            {t(strings.unchanged, { list: unchanged.join(" ") })}
          </p>
        ) : null}
      </section>
      <SymbolPalette strings={strings} />
      <p className={HINT}>{strings.accessibility}</p>
    </>
  );
}
